import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  LanguageSelect,
  LocaleCode,
  useAccountSettings,
} from '@/entities/account';
import { useLocale } from '@/shared/stores/accountStore';
import { FormError } from '@/shared/ui';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/lib/Button';

import { ChangeLanguageDialogProps } from '../model/types';
import { useChangeLanguage } from '../model/useChangeLanguage';

const CHANGE_LANGUAGE_DIALOG = 'changeLanguageDialog';
const CHANGE_LANGUAGE_FORM = 'changeLanguageForm';

export const ChangeLanguageDialog = ({
  open,
  onClose,
}: ChangeLanguageDialogProps) => {
  const { t } = useTranslation();
  const localeStore = useLocale();
  const { accountSettings } = useAccountSettings();
  const { changeLanguage } = useChangeLanguage();

  const systemLocale = accountSettings?.locale;

  const {
    control,
    handleSubmit,
    reset: resetForm,
    formState: { errors },
    setValue,
  } = useForm<{ locale: LocaleCode }>({
    defaultValues: {
      locale: (systemLocale as LocaleCode) ?? (localeStore as LocaleCode),
    },
  });

  useEffect(() => {
    if (systemLocale) {
      setValue('locale', systemLocale as LocaleCode);
    }
  }, [setValue, systemLocale]);

  const onSubmit = handleSubmit(async ({ locale }) => {
    await changeLanguage(locale);
    onClose?.();
  });

  const handleCloseDialog = () => {
    onClose?.();
    resetForm();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      id={CHANGE_LANGUAGE_DIALOG}
      title={t('settings:changeLanguage.title')}
      leftButtonSlot={
        <Button onClick={handleCloseDialog} variant="outlined">
          {t('common:buttons.cancel')}
        </Button>
      }
      rightButtonSlot={
        <Button
          type="submit"
          form={CHANGE_LANGUAGE_FORM}
          variant="contained"
          color="primary"
        >
          {t('common:buttons.save')}
        </Button>
      }
    >
      <form id={CHANGE_LANGUAGE_FORM} onSubmit={onSubmit}>
        <Controller
          name="locale"
          control={control}
          render={({ field }) => (
            <LanguageSelect value={field.value} onChange={field.onChange} />
          )}
        />
        {errors.locale?.message && (
          <FormError message={errors.locale.message} />
        )}
      </form>
    </Dialog>
  );
};
